import { ShippingMethod } from 'shared/types/cart/CustomShippingMethod';
import { Cart } from 'types/entity/cart';
import { UseCartReturn } from 'frontastic/hooks/useCart/types';
import { calculateDiscountedAmount, discountedShippingAmount } from './calculateDiscountedAmount';
import { calculateSubtotalAmount } from './calculateSubTotalAmount';

type MapCosts = (props: {
  cart?: Partial<Cart>;
  currency?: string;
  shippingMethod?: ShippingMethod;
}) => UseCartReturn['transaction'];

const mapCosts: MapCosts = ({ cart: data, currency = 'USD', shippingMethod }) => {
  // Absence of a platform total means "no cart to price yet" — not "a cart worth zero". A cart
  // whose items are all fully discounted (e.g. a free-samples code) legitimately totals 0, and
  // treating that as empty blanked the entire summary: no subtotal, no discount, no shipping.
  if (data?.sum?.centAmount === undefined) {
    return {
      subtotal: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      discount: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      totalNoTax: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      tax: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      shipping: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      total: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
    };
  }

  const currencyCode = data.sum?.currencyCode ?? currency;
  const fractionDigits = data.sum?.fractionDigits ?? 2;

  const totalAmount = data.sum?.centAmount as number;
  const subTotalAmount = calculateSubtotalAmount(data.lineItems, data.customLineItems);

  const discountedAmount = calculateDiscountedAmount(data);

  const totalTax = totalAmount > 0 ? (data.taxed?.taxAmount?.centAmount ?? 0) : 0;

  /**
   * The platform-charged shipping (base rate minus shipping discounts), so a waived rate renders
   * as free rather than silently keeping the base rate.
   *
   * A selected rate the platform has not priced yet — the commit is still in flight, or it failed
   * — falls back to that rate's own quote. Reading the cart regardless would report the
   * *previous* method's cost, which showed a free rate as still free after the shopper had
   * switched to a paid one. The one exception is a rate the platform's own applied discount
   * already covers, which the options list is labelling FREE: charging its carrier quote here
   * would make the summary contradict the list for the length of the commit.
   */
  const platformHasPricedSelection = !shippingMethod || data.shippingInfo?.name === shippingMethod.method;
  const selectionShipsFree = !!shippingMethod && !!data.freeShippingMethodNames?.includes(shippingMethod.method);
  const totalShipping = platformHasPricedSelection
    ? discountedShippingAmount(data)
    : selectionShipsFree
      ? 0
      : (shippingMethod?.estimatedCostNumericCentAmount ?? 0);

  const isEstimatedShipping = !!shippingMethod;

  const totalWithoutTaxes = subTotalAmount - discountedAmount + totalShipping;

  return {
    subtotal: {
      centAmount: subTotalAmount,
      currencyCode,
      fractionDigits,
    },
    discount: {
      centAmount: discountedAmount,
      currencyCode,
      fractionDigits,
    },
    shipping: {
      centAmount: totalShipping,
      currencyCode,
      fractionDigits,
    },
    isEstimatedShipping,
    totalNoTax: {
      centAmount: totalWithoutTaxes,
      currencyCode,
      fractionDigits,
    },
    tax: {
      centAmount: totalTax,
      currencyCode,
      fractionDigits,
    },
    total: {
      centAmount: totalWithoutTaxes + totalTax,
      currencyCode,
      fractionDigits,
    },
  };
};

export default mapCosts;
